import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { createWorldObject, updateWorldObject, listWorldObjects } from '../services/worldObjects';
import { adminPanel, AdminClose } from './adminChrome';

// GuardPanel — on-screen move controls for ANY door's bouncer (?guard=edit, or
// Bouncers / door in the admin hub).
//
// Khari, 2026-08-03: "It would be better add a 'bouncer' item in the admin. This
// way, i can add a gate to any building, using the bouncer as the repeated
// method. The current concert guard panel could be adjusted/enhanced to fit this
// more global use."
//
// So the only thing that changed is that it asks WHICH DOOR first. Pick a
// building, nudge the body, save — the row carries `ref`, and Bouncers renders
// every ref that has one. Placing a bouncer at a new building is now this panel,
// not a component.
//
// The RULE stays separate: who gets past a door lives in world_gates, keyed by
// the same ref. A body here with no gate there is a doorman who lets everyone
// in, which is a legitimate thing to want.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

const panel = adminPanel({
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 42,
  width: 196, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
});
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '9px 0', cursor: 'pointer',
  font: '600 14px system-ui', textAlign: 'center', userSelect: 'none',
};
const lab = { fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7, margin: '8px 0 4px' };

export default function GuardPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'guard';
  const g = useWorldStore((s) => s.guardTune);
  const set = useWorldStore((s) => s.setGuardTune);
  const objectId = useWorldStore((s) => s.guardObjectId);
  const dirty = useWorldStore((s) => s.guardDirty);
  const setGuardSaved = useWorldStore((s) => s.setGuardSaved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const ref = useWorldStore((s) => s.guardRef);
  const setRef = useWorldStore((s) => s.setGuardRef);
  const applyGuardTune = useWorldStore((s) => s.applyGuardTune);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const venues = Object.keys(venueAnchors || {}).sort();

  // Switching doors loads that door's saved body, so the pad is always editing
  // the thing you are looking at rather than the last thing you moved.
  useEffect(() => {
    if (!ON || !ref) return undefined;
    let dead = false;
    listWorldObjects('street', 'guard').then((rows) => {
      if (dead) return;
      const row = (rows || []).find((r) => r.ref === ref);
      if (row) applyGuardTune({ x: row.pos[0], y: row.pos[1], z: row.pos[2], rot: row.rotY, h: row.size }, row.object_id);
      else applyGuardTune({ x: -0.75, y: 0.1, z: 5, rot: 0, h: 0.68 }, null);
    });
    return () => { dead = true; };
  }, [ON, ref, applyGuardTune]);

  // Saved as an OFFSET from the concert hall's anchor — the anchor is derived
  // from the city model at runtime, so an absolute position would drift the
  // moment the model did. Copy-values still exists for pasting into a default.
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const body = { zone_id: 'street', kind: 'guard', ref, slot: 0,
        pos: [g.x, g.y, g.z], rotY: g.rot, size: g.h };
      const saved = objectId ? await updateWorldObject(objectId, body) : await createWorldObject(body);
      setGuardSaved(saved?.object_id ?? objectId);
      setMsg('saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };
  const [fine, setFine] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!ON) return null;

  const step = fine ? 0.25 : 1;
  const r = (v) => Math.round(v * 100) / 100;
  const move = (dx, dz) => set({ x: r(g.x + dx * step), z: r(g.z + dz * step) });
  const raise = (dir) => set({ y: r(g.y + dir * (fine ? 0.05 : 0.2)) });
  const copy = () => {
    navigator.clipboard?.writeText(`x: ${r(g.x)}, y: ${r(g.y)}, z: ${r(g.z)}, rot: ${r(g.rot)}, h: ${r(g.h)}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {});
  };

  return (
    <div style={panel}>
      <AdminClose />
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7 }}>Bouncer</div>
      <select
        value={ref || ''}
        onChange={(e) => setRef(e.target.value)}
        style={{ width: '100%', margin: '6px 0 2px', padding: '6px 8px', borderRadius: 8,
          background: 'rgba(255,178,94,0.12)', color: '#ffd27d',
          border: '1px solid rgba(255,178,94,0.4)', font: '600 12px system-ui' }}
      >
        {venues.length === 0 && <option value="">(city still loading)</option>}
        {venues.map((v) => <option key={v} value={v} style={{ color: '#1c1206' }}>{v}</option>)}
      </select>
      <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11.5, opacity: 0.85, margin: '4px 0 6px' }}>
        x {r(g.x)} · y {r(g.y)} · z {r(g.z)} · rot {r(g.rot)} · h {r(g.h)}
      </div>

      <div style={lab}>Move</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
        <span />
        <button style={btn} onClick={() => move(0, -1)}>↑</button>
        <span />
        <button style={btn} onClick={() => move(-1, 0)}>←</button>
        <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>x/z</div>
        <button style={btn} onClick={() => move(1, 0)}>→</button>
        <span />
        <button style={btn} onClick={() => move(0, 1)}>↓</button>
        <span />
      </div>

      <div style={lab}>Raise / lower</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => raise(-1)}>▼ down</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => raise(1)}>up ▲</button>
      </div>

      <div style={lab}>Turn</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ rot: r(g.rot - (fine ? 0.1 : Math.PI / 2)) })}>⟲</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ rot: r(g.rot + (fine ? 0.1 : Math.PI / 2)) })}>⟳</button>
      </div>

      <div style={lab}>Size</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ h: Math.max(0.5, r(g.h - (fine ? 0.02 : 0.1))) })}>−</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ h: r(g.h + (fine ? 0.02 : 0.1)) })}>+</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '10px 0', cursor: 'pointer', opacity: 0.9 }}>
        <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
        fine steps
      </label>
      <button
        disabled={busy}
        style={{ ...btn, width: '100%', marginBottom: 5, fontSize: 12,
          background: dirty ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: dirty ? '#1c1206' : '#ffd27d' }}
        onClick={save}
      >
        {busy ? '…' : dirty ? 'Save *' : 'Save'}
      </button>
      {msg && <div style={{ fontSize: 10.5, marginBottom: 6, opacity: 0.85, color: msg === 'saved' ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}
      <button style={{ ...btn, width: '100%', background: copied ? '#9dff7d' : 'rgba(255,178,94,0.14)', color: copied ? '#0c2c06' : '#ffd27d', fontSize: 12 }} onClick={copy}>
        {copied ? '✓ copied' : 'copy values'}
      </button>
    </div>
  );
}
